<template>
  <div
    ref="panelRef"
    class="item-execution-map-panel"
  >
    <MapBase
      ref="mapBase"
      :show-geo-search-control="false"
      class="item-execution-map-panel__map"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import L from 'leaflet'
import MapBase from '@/global/components/map/MapBase.vue'
import polyUtil from 'polyline-encoded'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'

// `send-data` from ExecRoutesBridge (see its own comment), forwarded here by
// DataTable as `custom-data` - one entry per *checked* exec-route on the
// index screen (empty until a row's checkbox is selected), or a single-item
// array built directly from formData on the details screen. Each entry can
// carry its own `locations` (index screen, fetched per selected route by
// ExecRoutesBridge) - the dedicated `locations` prop below covers the
// details screen instead, which has no per-route wrapper to hang it off of.
const props = defineProps({
  customData: {
    type: [Array, null],
    default: () => []
  },
  // Route-to-details click-through, opt-in per consumer - off by default
  // (neither the index screen's grid map nor the details screen's own map
  // needs it: the grid already navigates to details via its own row click,
  // and the details screen is already showing that one route's details).
  clickableRoutes: {
    type: Boolean,
    default: false
  },
  // One entry per item-execution location (backend's `locations` array),
  // rendered as its own colored marker - color follows the location's own
  // `status.color`, independent of the route polyline's color.
  locations: {
    type: [Array, null],
    default: () => []
  }
})

const DEFAULT_ROUTE_COLOR = '#3388ff'
// The route polyline is always this fixed color, on both the index and
// details screens - deliberately not the route's status color, so it stays
// visually distinct from the per-location markers, which already carry
// their own `status.color`.
const ROUTE_COLOR = '#A3E635'

const { router } = useAppContext()

const mapBase = ref(null)
const panelRef = ref(null)
let resizeObserver = null
let isMapReady = false

function buildLocationIcon (color) {
  return L.divIcon({
    className: 'marker-icon',
    iconSize: [30, 30],
    // The `.custom-marker` span isn't centered in this 30x30 box - its
    // `margin: -20px 0 0 -20px` (see CSS below) puts its own center at
    // (10, 10), and its sharp untouched corner (bottom-left, the only one
    // NOT rounded by `border-radius: 50% 50% 50% 0`) is what forms the
    // pin's tip after the -45deg rotation. Rotating that corner around
    // (10, 10) lands the tip at (10, 31.2), not the box's own bottom-center
    // (15, 30/33) - a wrong anchor here is a fixed pixel-space offset, so it
    // stays invisible zoomed in (tiny ground distance per pixel) but drifts
    // the marker away from its real coordinate as you zoom out.
    iconAnchor: [10, 31],
    html: `<span style="background-color: ${color || DEFAULT_ROUTE_COLOR}" class="custom-marker"></span>`
  })
}

function renderPolylines () {
  if (!isMapReady || !mapBase.value) {
    return
  }

  mapBase.value.removeAllPolylines()

  const polylines = []
  const markers = []

  ;(props.customData || [])
    .filter(route => route?.polyline)
    .forEach(route => {
      const coordinates = polyUtil.decode(route.polyline)

      polylines.push({
        coordinates,
        options: {
          id: `exec-route-${route.id}`,
          color: ROUTE_COLOR,
          weight: 4
        },
        ...(props.clickableRoutes && {
          polylineClick: () => router.push({
            name: 'TemporaryRegulationsItemExecutionDetails',
            params: { id: String(route.id) }
          })
        })
      })
    })

  const routeLocations = (props.customData || []).flatMap(route => route?.locations || [])

  ;[...(props.locations || []), ...routeLocations]
    .forEach(location => {
      const latitude = location?.coordinates?.latitude
      const longitude = location?.coordinates?.longitude

      if (latitude == null || longitude == null) {
        return
      }

      markers.push({
        coordinates: [latitude, longitude],
        options: {
          icon: buildLocationIcon(location.status?.color?.icon),
          id: `exec-location-${location.id}`
        },
        // `handlePolylineObjectsTooltips` (MapBase.vue) only wires up a
        // hover tooltip when `popup.popupData` is present - it awaits the
        // return value, so a plain (non-async) function works too.
        popup: {
          popupData: () => location.address || location.name,
          // Keeps `polyline-tooltip`'s rounded corners (MapBase.vue) and adds
          // the wrapping/width cap below - a full address on one Leaflet
          // nowrap line runs off the side of this narrow map panel.
          popupOptions: {
            className: 'polyline-tooltip item-execution-tooltip'
          }
        }
      })
    })

  if (polylines.length || markers.length) {
    mapBase.value.generatePolylines({ polylines, markers })
  }
}

onMounted(() => {
  // MapBase's own `ready` event still runs its default company/Zagreb
  // re-centering right after firing - registering through its `onMapReady`
  // callback queue instead skips that default centering, so our fitBounds
  // (done inside generatePolylines via `fitPolylines: true`) isn't overridden.
  mapBase.value?.onMapReady(() => {
    isMapReady = true
    renderPolylines()
  })

  // The panel is laid out against whatever sits next to it, so its size moves with
  // the screen-view picker beside the listing - and Leaflet only loads the tiles for
  // the size it last knew about, which leaves the new half of the map grey.
  if (typeof ResizeObserver === 'undefined' || !panelRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    mapBase.value?.invalidateSize?.()
  })
  resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(() => [props.customData, props.locations], renderPolylines, { deep: true })
</script>

<style scoped>
.item-execution-map-panel {
  display: flex;
  height: 100%;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  /* Leaflet's own controls/panes reach z-index 1000-9999 internally (see
     MapBase.vue's `.leaflet-tooltip` override). Without its own stacking
     context, that competes directly with page-level chrome like the sticky
     app header (which has no explicit z-index, so it defaults to Vuetify's
     5) once the page scrolls - `isolation: isolate` caps all of this map's
     descendants to stack only against each other, never against anything
     outside this component. */
  isolation: isolate;
}

.item-execution-map-panel__map {
  flex: 1 1 auto;
}
</style>

<style lang="scss">
/* Leaflet tooltips default to `white-space: nowrap`, so a long address grew
   the tooltip wider than the map panel itself and the text got clipped at the
   panel edge. Wrap it inside a fixed cap instead. */
/* Compound with `.leaflet-tooltip` so it outranks Leaflet's own nowrap rule
   regardless of stylesheet order. */
.leaflet-tooltip.item-execution-tooltip {
  /* `max-content` first, then capped: a plain `max-width` on an absolutely
     positioned tooltip shrinks to the space Leaflet happens to leave beside
     the marker, which collapsed the text into a one-letter-wide column. Sizing
     from the content instead keeps short labels on one line and wraps only
     what's longer than the cap. */
  width: max-content;
  max-width: 240px;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: center;
}

/* Unscoped: these divIcons are appended by Leaflet outside this component's
   scoped DOM tree, so a scoped rule here would never reach them. */
.marker-icon {
  z-index: 201 !important;
}

.custom-marker {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  position: absolute;
  transform: rotate(-45deg);
  left: 50%;
  top: 50%;
  margin: -20px 0 0 -20px;
}

.custom-marker:after {
  content: "";
  width: 8px;
  height: 8px;
  margin: 11px 0 0 11px;
  background: #e6e6e6;
  position: absolute;
  border-radius: 50%;
}
</style>
